import { useEffect, useRef, useState } from 'react';
import { displayName, emailLink, google, signOut, useAccount } from '../game/account';

/**
 * The account corner of the title screen. Guests and signed-in players look clearly
 * different at a glance: guests get an outlined GUEST tag and a sign-in prompt, accounts
 * a solid tag with their callsign.
 */
export function AccountChip() {
  const ready = useAccount((s) => s.ready);
  const session = useAccount((s) => s.session);
  const role = useAccount((s) => s.role);
  const name = useAccount(displayName);
  const [menu, setMenu] = useState(false);
  if (!ready) return null;
  if (!session) {
    return (
      <div className="account-chip guest">
        <span className="acct-tag guest">Guest</span>
        <button type="button" className="linkish" onClick={() => useAccount.setState({ signInOpen: true })}>
          Sign in
        </button>
        <span className="acct-why">to contribute and keep progress</span>
      </div>
    );
  }
  return (
    <div className="account-chip member">
      <button type="button" className="acct-tag member" aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
        {role === 'owner' ? 'Owner' : 'Account'} · {name}
      </button>
      {menu && (
        <div className="acct-menu" role="menu">
          <a role="menuitem" href="/contribute">
            Contribute sounds or a face
          </a>
          {role === 'owner' && (
            <a role="menuitem" href="/review">
              Review contributions
            </a>
          )}
          <button role="menuitem" type="button" onClick={() => void signOut().then(() => setMenu(false))}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** Email-link and Google sign-in. No passwords. */
export function SignInSheet() {
  const open = useAccount((s) => s.signInOpen);
  const [email, setEmail] = useState('');
  const [state, setState] = useState<{ kind: 'idle' | 'sending' | 'sent' } | { kind: 'error'; msg: string }>({ kind: 'idle' });
  const input = useRef<HTMLInputElement>(null);
  const close = () => {
    useAccount.setState({ signInOpen: false });
    setState({ kind: 'idle' });
  };
  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open]);
  if (!open) return null;
  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setState({ kind: 'sending' });
    const err = await emailLink(email);
    setState(err ? { kind: 'error', msg: err } : { kind: 'sent' });
  };
  return (
    <div className="overlay notes-overlay" role="dialog" aria-modal="true" aria-labelledby="signin-title" onClick={(e) => e.target === e.currentTarget && close()}>
      <form className="card signin" onSubmit={send}>
        <div className="eyebrow">Account</div>
        <h2 id="signin-title">Sign in</h2>
        {state.kind === 'sent' ? (
          <>
            <p>
              Check <b>{email}</b> for a sign-in link. Open it on this device and you'll come straight back here, signed in.
            </p>
            <div className="row">
              <button className="cta" type="button" onClick={close}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="note">No password needed. You can keep playing as a guest; an account is for contributing and, later, keeping your progress.</p>
            <label className="field">
              <span className="lbl">Email</span>
              <input ref={input} type="email" required autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            {state.kind === 'error' && (
              <p className="note err" role="alert">
                {state.msg}
              </p>
            )}
            <div className="row">
              <button className="cta" type="submit" disabled={state.kind === 'sending' || !email.includes('@')}>
                {state.kind === 'sending' ? 'Sending…' : 'Email me a link'}
              </button>
              <button
                className="cta alt"
                type="button"
                onClick={async () => {
                  const err = await google();
                  if (err) setState({ kind: 'error', msg: err });
                }}
              >
                Continue with Google
              </button>
            </div>
            <button type="button" className="linkish" onClick={close}>
              Keep playing as a guest
            </button>
          </>
        )}
      </form>
    </div>
  );
}
